import { IWeatherChartDataResModel, IWeatherHierarchiesResModel, IWeatherStationReqModel } from '@/util/Request/requestType';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import { ICoordinate } from '@/components/GoogleMapReact/GoogleMapReactComponent';

interface IWeatherStationReduxModel {
    polyFuncEnable?: boolean
    mapLoading?: boolean
    chart: boolean
    isCelsius: boolean
    speed: ISpeed
    variableMapToUm?: { string: string }
    queryId?: string
    treeData?: ITreeData[]
    chartData?: IWeatherChartDataResModel[]
    requestOptions?: IWeatherStationReqModel
    stationReqDate?: {startDate: string, endDate: string}
    stationTabStations?: IStation[]
    oauthUrl?: string
    // draw on map
    drawReactModel?: boolean,
    drawCircleMode?: boolean
    polyPoint?: ICoordinate[]
    fromQueryHistory?: boolean,
    center?: ICoordinate
    radius?: number
    weatherHierchyData?: IWeatherHierarchiesResModel
}

interface IStation {
    stationID: string
    latitude: string
    longitude: string
    variables: string[]
    aggregation?: string
    frequency?: string
    suppler?: string
}

enum ISpeed {
    kmh = 0,
    ms = 1
}

interface ITreeData {
    title: string
    key: string
    icon: any
    children: ITreeData[]
    area_IfHasChildLevel: boolean
}

const initialState: IWeatherStationReduxModel = {
    polyFuncEnable: false,
    mapLoading: false,
    chart: false,
    isCelsius: true,
    speed: 0,
    variableMapToUm: null,
    stationTabStations: [],
    drawReactModel: false,
    drawCircleMode: false
};

export const weatherStation = createSlice({
    name: 'weatherStation',
    initialState,
    reducers: {
        updatePolyFuncEnableState: (state, action: PayloadAction<boolean>) => {
            return {
                ...state,
                polyFuncEnable: action.payload
            };
        },
        updateMapLoadingState: (state, action: PayloadAction<boolean>) => {
            return {
                ...state,
                mapLoading: action.payload
            };
        },
        updateUnderChartState: (state, action: PayloadAction<boolean>) => {
            return {
                ...state,
                chart: action.payload
            };
        },
        updateIsCelsius: (state, action: PayloadAction<boolean>) => {
            return {
                ...state,
                isCelsius: action.payload
            };
        },
        updateSpeedUnit: (state, action: PayloadAction<ISpeed>) => {
            return {
                ...state,
                speed: action.payload
            };
        },
        updateVariableToUm: (state, action: PayloadAction<{ string: string }>) => {
            return {
                ...state,
                variableMapToUm: action.payload
            };
        },
        updateSavedQueryId: (state, action: PayloadAction<string>) => {
            return {
                ...state,
                queryId: action.payload
            };
        },
        updateTreeData: (state, action: PayloadAction<ITreeData[]>) => {
            return {
                ...state,
                treeData: action.payload
            };
        },
        updateChartData: (state, action: PayloadAction<IWeatherChartDataResModel[]>) => {
            return {
                ...state,
                chartData: action.payload
            };
        },
        updateRequestOptions: (state, action: PayloadAction<IWeatherStationReqModel>) => {
            return {
                ...state,
                requestOptions: action.payload
            };
        },
        updateStationReqDate: (state, action: PayloadAction<{startDate: string, endDate: string}>) => {
            return {
                ...state,
                stationReqDate: action.payload
            };
        },
        updateSelectedStations: (state, action: PayloadAction<IStation>) => {
            const { stationTabStations } = state;
            const station = action.payload;
            const res = stationTabStations.filter((item) => station.stationID === item.stationID);
            if (res.length > 0) {
                return state;
            } else {
                const newStationSet = [...stationTabStations, station];
                return {
                    ...state,
                    stationTabStations: newStationSet
                };
            }
        },
        updateDeselectedStations: (state, action: PayloadAction<IStation>) => {
            const { stationTabStations } = state;
            const station = action.payload;
            const res = stationTabStations.filter((item) => station.stationID === item.stationID);
            if (res.length > 0) {
                return state;
            } else {
                const index = stationTabStations.findIndex((item, index) => {
                    if(item.stationID === station.stationID) {
                        return index;
                    }
                });
                let newStationTabStations = [...stationTabStations];
                newStationTabStations = newStationTabStations.splice(index, 1);
                return {
                    ...state,
                    stationTabStations: newStationTabStations
                };
            }
        },
        updateOauthUrl: (state, action: PayloadAction<string>) => {
            return {
                ...state,
                oauthUrl: action.payload
            };
        },
        updateDrawOnMapData: (state, action: PayloadAction<{
            polyPoint?: ICoordinate[]
            isMapView?: boolean
            circlePoint?: ICoordinate[]
            center?: ICoordinate
            radius?: number
            fromQueryHistory?: boolean
        }>) => {
            return {
                ...state,
                ...action.payload
            };
        },
        updateWeatherHierchydata: (state, action: PayloadAction<IWeatherHierarchiesResModel>) => {
            return {
                ...state,
                weatherHierchyData: action.payload
            };
        }
    }
});

export type {
    IWeatherStationReduxModel,
    IStation
};

export const {
    updatePolyFuncEnableState,
    updateMapLoadingState,
    updateUnderChartState,
    updateIsCelsius,
    updateSpeedUnit,
    updateVariableToUm,
    updateSavedQueryId,
    updateTreeData,
    updateChartData,
    updateRequestOptions,
    updateStationReqDate,
    updateSelectedStations,
    updateDeselectedStations,
    updateOauthUrl,
    updateDrawOnMapData,
    updateWeatherHierchydata
} = weatherStation.actions;

export const selectWeatherStation = (state: RootState) => state.weatherStation;

export default weatherStation.reducer;